import React, { useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

// Password policy values mirrored from backend/app/core/password_policy.py.
const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 64;
const PASSWORD_BLANK = 'PASSWORD_BLANK';
const PASSWORD_TOO_SHORT = 'PASSWORD_TOO_SHORT';
const PASSWORD_TOO_LONG = 'PASSWORD_TOO_LONG';
const PASSWORD_TOO_COMMON = 'PASSWORD_TOO_COMMON';
const PASSWORD_CONTAINS_PERSONAL_INFO = 'PASSWORD_CONTAINS_PERSONAL_INFO';

// Frontend-only validation code for the confirm password field.
const PASSWORD_MISMATCH = 'PASSWORD_MISMATCH';

// User-facing copy for password validation failures.
const PASSWORD_ERROR_MESSAGES = {
  [PASSWORD_BLANK]: 'Password cannot be blank.',
  [PASSWORD_TOO_SHORT]: `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`,
  [PASSWORD_TOO_LONG]: `Password must be no more than ${MAX_PASSWORD_LENGTH} characters.`,
  [PASSWORD_TOO_COMMON]: `Choose a less common password.`,
  [PASSWORD_CONTAINS_PERSONAL_INFO]: `Choose a password that doesn’t include your name, email, or other personal information.`,
  [PASSWORD_MISMATCH]: 'Passwords do not match.',
};

/**
 * Extracts a readable reset-password error from FastAPI responses.
 * FastAPI can return detail as a string or as a validation error array.
 */
const getResetPasswordErrorMessage = (error) => {
  const detail = error.response?.data?.detail;

  if (typeof detail === 'string') {
    return detail;
  }

  if (detail.errors.length === 1) {
    return PASSWORD_ERROR_MESSAGES[detail.errors[0]];
  }

  if (Array.isArray(detail) && detail.length > 0) {
    return detail.map((item) => item.msg).filter(Boolean).join(' ') || 'Please check your password and try again.';
  }

  return error.response?.data?.message || 'Unable to reset password. Please request a new reset link.';
};

/**
 * Validates the new password and confirmation fields before submitting to the backend.
 */
const validatePassword = (newPassword, confirmPassword) => {
  const errors = [];

  // Match the backend blank-password check, which treats whitespace-only values as blank.
  if (!newPassword.trim()) {
    errors.push(PASSWORD_BLANK);
  }

  // Match the backend minimum password length policy.
  if (newPassword.length < MIN_PASSWORD_LENGTH) {
    errors.push(PASSWORD_TOO_SHORT);
  }

  // Match the backend maximum password length policy.
  if (newPassword.length > MAX_PASSWORD_LENGTH) {
    errors.push(PASSWORD_TOO_LONG);
  }

  // Confirm the user typed the same password twice before calling the API.
  if (newPassword !== confirmPassword) {
    errors.push(PASSWORD_MISMATCH);
  }

  return errors;
};

/**
 * ResetPasswordPage Component - A form for users to reset their password from an emailed token link.
 */
const ResetPasswordPage = () => {
  // State hooks to manage form inputs, validation, and submission feedback.
  const [newPassword, setNewPassword] = useState(''); // Store new password state
  const [confirmPassword, setConfirmPassword] = useState(''); // Store confirm password state
  const [error, setError] = useState(''); // Store error messages
  const [passwordErrors, setPasswordErrors] = useState([]); // Store password policy validation errors
  const [isSubmitting, setIsSubmitting] = useState(false); // State to track reset-password submission

  const { token: accessToken, login, logout } = useContext(AuthContext); // Use the current session token and auth functions from AuthContext
  const navigate = useNavigate(); // Hook to navigate to different pages (after successful reset)
  const [searchParams] = useSearchParams(); // Hook to read the token query parameter from the emailed reset link
  const resetToken = searchParams.get('token'); // Store reset token from /reset-password?token=
  const hasExistingSession = Boolean(accessToken); // Track whether this browser already has an active app session
  const [tokenError, setTokenError] = useState(!resetToken ? 'This password reset link is missing a token. Please request a new reset link.' : '');
  const [isTokenChecking, setIsTokenChecking] = useState(Boolean(resetToken));

  /**
   * Validates that token is valid as form is loaded.
   */
  useEffect(() => {
    const validateToken = async () => {
      if (!resetToken) {
        return;
      }

      try {
        await axios.get(`${API_BASE_URL}/api/v1/auth/reset-password/${encodeURIComponent(resetToken)}/validate`);
        setTokenError('');
      } catch (err) {
        setTokenError(getResetPasswordErrorMessage(err));
      } finally {
        setIsTokenChecking(false);
      }
    };

    validateToken();
  }, [resetToken]);

  /**
   * Handles form submission for resetting the user's password.
   * Sends the new password to the token-specific FastAPI reset endpoint.
   * If the token and password are valid, replaces the current browser session with the returned tokens and redirects to the home page.
   *
   * @param {React.FormEvent} e - The form submission event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Run frontend password checks before calling the backend.
    const validationErrors = validatePassword(newPassword, confirmPassword);
    setPasswordErrors(validationErrors);

    if (validationErrors.length > 0 || tokenError) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Send the new password to the FastAPI reset endpoint using the token from the email link.
      const response = await axios.post(`${API_BASE_URL}/api/v1/auth/reset-password/${encodeURIComponent(resetToken)}`, {
        new_password: newPassword,
      });

      // Check if the response contains the JWT tokens and log the user in.
      if (response.data.access_token && response.data.refresh_token) {
        // Clear any existing browser session before replacing it with the reset-password session.
        if (hasExistingSession) {
          logout();
        }

        // Store JWT tokens in localStorage.
        localStorage.setItem('access_token', response.data.access_token);
        localStorage.setItem('refresh_token', response.data.refresh_token);

        // Call the login function from context to update the app state.
        login(response.data.access_token, '');

        // Navigate to the home page upon successful password reset.
        navigate('/');
      }
    } catch (err) {
      // Handle errors if the password reset attempt fails.
      setError(getResetPasswordErrorMessage(err));

      // Clear the form inputs after a failed reset attempt.
      setNewPassword('');
      setConfirmPassword('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // Main container to center the reset password form on the screen.
    <Container className="d-flex justify-content-center align-items-center min-vh-100">
      <Row className="w-100">
        <Col md={6} lg={4} className="mx-auto">
          {/* Card component for displaying the reset password form. */}
          <Card className="shadow-sm">
            <Card.Body>
              {/* Logo and heading. */}
              <div className="text-center mb-4">
                <img
                  alt="AGRISMART logo"
                  src="/android-chrome-192x192.png"
                  width="60"
                  height="60"
                  className="text-primary"
                />
                <h2 className="mt-2">Reset password</h2>
              </div>

              {/* Show loading feedback while checking whether the emailed reset token is valid. */}
              {isTokenChecking && (
                <Alert variant="info" className="text-center">
                  Checking reset link...
                </Alert>
              )}

              {/* Show a dead-end reset link state when the token is missing, expired, or invalid. */}
              {!isTokenChecking && tokenError && (
                <div className="text-center">
                  <Alert variant="danger">
                    <h5 className="mb-2">Reset link unavailable</h5>
                    <div>{tokenError}</div>
                  </Alert>

                  {/* Let the signed-out user request a fresh reset email if this link cannot be used. */}
                  <Button as={Link} to="/forgot-password" variant="primary" className="w-100 mb-2">
                    Request new reset link
                  </Button>

                  {/* Let the signed-out user return to login without trying to reset their password. */}
                  <Button as={Link} to="/login" variant="link" className="p-0">
                    Back to login
                  </Button>
                </div>
              )}

              {/* Form for new password and password confirmation. */}
              {!isTokenChecking && !tokenError && (
                <Form onSubmit={handleSubmit}>
                  {hasExistingSession && (
                    <Alert variant="info">
                      This reset will replace the current browser session after your password is updated.
                    </Alert>
                  )}

                  {/* New password input field. */}
                  <Form.Group controlId="new-password" className="mb-3">
                    <Form.Label>New password</Form.Label>
                    <Form.Control
                      type="password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={MIN_PASSWORD_LENGTH}
                      maxLength={MAX_PASSWORD_LENGTH}
                      autoComplete="new-password"
                      placeholder="Enter new password"
                    />
                  </Form.Group>

                  {/* Confirm password input field. */}
                  <Form.Group controlId="confirm-password" className="mb-3">
                    <Form.Label>Confirm password</Form.Label>
                    <Form.Control
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={MIN_PASSWORD_LENGTH}
                      maxLength={MAX_PASSWORD_LENGTH}
                      autoComplete="new-password"
                      placeholder="Confirm new password"
                    />
                  </Form.Group>

                  {/* Display password policy errors if any. */}
                  {passwordErrors.length > 0 && (
                    <Alert variant="danger">
                      {passwordErrors.map((passwordError) => (
                        <div key={passwordError}>{PASSWORD_ERROR_MESSAGES[passwordError]}</div>
                      ))}
                    </Alert>
                  )}

                  {/* Display reset endpoint error message if any. */}
                  {error && <Alert variant="danger">{error}</Alert>}

                  {/* Submit button. */}
                  <Button variant="primary" type="submit" className="w-100" disabled={isSubmitting || isTokenChecking || Boolean(tokenError)}>
                    {isSubmitting ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Resetting password
                      </>
                    ) : (
                      'Reset password'
                    )}
                  </Button>

                  {/* Login navigation. */}
                  <div className="text-center mt-3">
                    <Button as={Link} to="/login" variant="link" className="p-0">
                      Back to login
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ResetPasswordPage;
